import { useEffect } from 'react';

import { ArrowRight } from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';

import { Trans } from '@lingui/react/macro';
import { ComponentRendered } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import methodsHeroOffer from 'src/assets/img/methods-hero-offer.webp';
import { Button } from 'src/components';
import { Highlight } from 'src/components/Highlight';
import { ValueProps } from 'src/components/Offers/ValueProps';
import { Price } from 'src/components/Price';
import { StackedSheet } from 'src/components/StackedSheet';
import { useFreeTrialDuration } from 'src/utils/freeTrialLength';
import { SKU } from 'src/utils/skus';
import { URLS } from 'src/utils/urls/urls';
import { valueProps } from 'src/utils/valueProps';

export const WinbackOffer = ({
  componentLocation,
  freeTrialLengthDays,
}: {
  componentLocation: string;
  freeTrialLengthDays: number;
}) => {
  const trialDuration = useFreeTrialDuration(freeTrialLengthDays);
  useEffect(() => {
    tracker.trackEvent(
      new ComponentRendered({
        component_name: 'WinbackOffer',
        component_type: 'hero',
      })
    );
  }, []);
  return (
    <StackedSheet dataCy="winback-offer-landing">
      <div
        data-cy="winback-offer"
        className="flex flex-col-reverse gap-24 lxl:flex-row lxl:gap-64"
      >
        <div className="flex basis-[45%] flex-col justify-center">
          <img
            src={methodsHeroOffer}
            alt="Multiple templates stacked on top of each other"
            className="mx-auto aspect-3/4 w-full max-w-full min-w-[272px] lxl:w-auto"
          />
        </div>
        <div className="flex basis-[55%] flex-col items-start gap-24 text-left lm:gap-32">
          <div className="flex flex-col gap-24">
            <Tag size="large" variant="info-light">
              <Trans>Limited Time Offer</Trans>
            </Tag>
            <Typography
              variant="fluid-heading-2xl"
              className="my-24 text-left ll:my-16 ll:whitespace-nowrap"
            >
              <Trans>
                Claim your{' '}
                <Highlight scale={320}>{trialDuration} free</Highlight> trial
              </Trans>
            </Typography>
            <Typography className="m-0 text-left text-pretty" variant="body-md">
              <Trans>
                Tried Connect yet? Get exclusive templates from reMarkable
                Methods today, and take your thinking further with everything
                else Connect has to offer:
              </Trans>
            </Typography>
          </div>

          <div className="w-full">
            <ValueProps
              className="justify-start"
              valueProps={valueProps.connect}
            />
          </div>

          <Button
            as="a"
            variant="primary"
            to={URLS.STORE_METHODS_OFFER_CHECKOUT}
            className="my-24 w-full lm:w-fit ll:mt-32"
            data-cy="landing-next-step"
            analytics={{
              action: 'activate winback offer',
              location: componentLocation,
            }}
          >
            <span data-cy="winback-offer-activate-button">
              <Trans>Try Connect for free</Trans>
            </span>
            <ArrowRight />
          </Button>

          <Typography variant="body-sm" className="text-pretty">
            <Trans>
              Connect costs <Price sku={SKU.connectMonthly} />
              /mo. when your free trial ends. <b>Cancel anytime</b>.
            </Trans>
          </Typography>
        </div>
      </div>
    </StackedSheet>
  );
};
